import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import './index.css';
import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  createRoutesFromChildren, matchRoutes, useLocation, useNavigationType,
} from 'react-router-dom';
import * as serviceWorker from './serviceWorker';
import theme from './theme';
import App from './App';
import { createApolloCache } from './apolloCache';

// Redact the Hub magic-link token (and the petcademy-token, if it ever appears) from any
// URL string before it leaves the browser, so auth tokens are never shipped to Sentry.
const SENSITIVE_PARAM_RE = /([?&](?:token|petcademy-token)=)[^&#]*/gi;
const redact = (value: unknown) => (
  typeof value === 'string' ? value.replace(SENSITIVE_PARAM_RE, '$1[redacted]') : value
);
const scrubEvent = (event: Sentry.Event): Sentry.Event => {
  if (event.request?.url) {
    event.request.url = redact(event.request.url) as string;
  }
  if (typeof event.transaction === 'string') {
    event.transaction = redact(event.transaction) as string;
  }
  return event;
};

Sentry.init({
  dsn: 'https://5123581313ad41ebbd4d157ca97e3400@o1317948.ingest.sentry.io/6571447',
  integrations: [new BrowserTracing({
    routingInstrumentation: Sentry.reactRouterV6Instrumentation(
      React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    ),
  }), new Sentry.Integrations.Breadcrumbs({
    console: process.env.NODE_ENV !== 'development',
  })],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  environment: process.env.NODE_ENV === 'development' ? 'dev' : process.env.REACT_APP_CONTEXT,
  release: process.env.REACT_APP_COMMIT_REF,
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubEvent,
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data) {
      ['url', 'to', 'from'].forEach((key) => {
        if (typeof breadcrumb.data?.[key] === 'string') {
          breadcrumb.data[key] = redact(breadcrumb.data[key]);
        }
      });
    }
    return breadcrumb;
  },
});

const httpLink = createHttpLink({
  uri: '/.netlify/functions/graphql',
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const tokenString = localStorage.getItem('ni.goTrueToken');
  const tokenJSON = tokenString ? JSON.parse(tokenString) : {};
  const token = tokenJSON?.access_token;
  const petcademyToken = localStorage.getItem('petcademyToken');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'awo-id': localStorage.getItem('awoId'),
      'petcademy-token': petcademyToken,
    },
  };
});

const client = new ApolloClient({
  link: from([authLink, httpLink]),
  cache: createApolloCache(),
});

ReactDOM.render(
  <ApolloProvider client={client}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </ApolloProvider>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
